import { SSOConfig } from '../api/domains/auth';

export class SSOService {
    private static googleAuthInstance: any = null;
    private static microsoftAuthInstance: any = null;

    static async initializeGoogleAuth(_clientId: string): Promise<void> {
        if (typeof window === 'undefined') return;

        // Load Google Sign-In script
        await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = reject;
            document.head.appendChild(script);
        });

        // Initialize Google Sign-In
        this.googleAuthInstance = (window as any).google;
    }

    static async initializeMicrosoftAuth(config: SSOConfig): Promise<void> {
        if (typeof window === 'undefined') return;

        // Load MSAL script
        await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://alcdn.msauth.net/browser/2.30.0/js/msal-browser.min.js';
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = reject;
            document.head.appendChild(script);
        });

        // Initialize MSAL
        const msalConfig = {
            auth: {
                clientId: config.client_id,
                authority: `https://login.microsoftonline.com/${config.tenant_id}`,
                redirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: false,
            },
        };

        this.microsoftAuthInstance = new (window as any).msal.PublicClientApplication(msalConfig);
    }

    static async getGoogleToken(clientId: string): Promise<string> {
        if (!this.googleAuthInstance) {
            await this.initializeGoogleAuth(clientId);
        }

        return new Promise((resolve, reject) => {
            this.googleAuthInstance.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: 'email profile',
                callback: (response: any) => {
                    if (response.access_token) {
                        resolve(response.access_token);
                    } else {
                        reject(new Error('Failed to get Google token'));
                    }
                },
            }).requestAccessToken();
        });
    }

    static async getMicrosoftToken(config: SSOConfig): Promise<string> {
        if (!this.microsoftAuthInstance) {
            await this.initializeMicrosoftAuth(config);
        }

        try {
            const loginResponse = await this.microsoftAuthInstance.loginPopup({
                scopes: ['openid', 'profile', 'email', 'User.Read'],
            });

            return loginResponse.accessToken;
        } catch (error) {
            console.error('Microsoft login error:', error);
            throw new Error('Failed to get Microsoft token');
        }
    }

    static async getToken(config: SSOConfig): Promise<{ token: string; domain: string }> {
        try {
            let token: string;

            switch (config.provider) {
                case 'google':
                    token = await this.getGoogleToken(config.client_id);
                    break;
                case 'azure':
                    token = await this.getMicrosoftToken(config);
                    break;
                default:
                    throw new Error(`Unsupported SSO provider: ${config.provider}`);
            }

            // Extract domain from metadata URL
            const domain = new URL(config.metadata_url).hostname.split('.').slice(1).join('.');

            return { token, domain };
        } catch (error) {
            console.error('SSO error:', error);
            throw error;
        }
    }
} 